import {
  changeCustomTaskRepetition,
  getDefaultWeekdays
} from '@hconnect/common/components/repetitiveTask'
import {TaskRepetitionInterval} from '@hconnect/common/components/repetitiveTask/TaskRepetitionInterval'
import {CustomTaskRepetitionDropDownYearly} from '@hconnect/common/components/shiftEventFormFields'
import {CustomTaskRepetition, isCustomWeeklyRepetition} from '@hconnect/common/types'
import {GridSetting} from '@hconnect/uikit'
import {WeekDaySelect} from '@hconnect/uikit/src/lib2'
import {SimpleDatePicker} from '@hconnect/uikit/src/lib2/components/momentDatePicker'
import CheckIcon from '@mui/icons-material/Check'
import CancelIcon from '@mui/icons-material/Close'
import {Button, Dialog, DialogContent, DialogTitle, DialogActions, Grid, Box} from '@mui/material'
import moment from 'moment-timezone'
import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../../consts'
import {ProcessWorkOrderDueDatesPreviewLabel} from '../../ProcessWorkOrderDueDatesPreviewLabel'

import {TaskRepetitionEndingOptions} from './TaskRepetitionEndingOptions'

type CustomRecurringTaskProps = {
  timezone: string
  settings: CustomTaskRepetition
  plantId: string
  onChange: (next: CustomTaskRepetition) => void
}

const regularSize: GridSetting = {xs: 12}

const CustomRecurringTask: React.FC<CustomRecurringTaskProps> = ({
  timezone,
  settings,
  plantId,
  onChange
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const defaultEnd = moment().tz(timezone).endOf('day')
  return (
    <Grid container justifyContent="flex-start" alignItems="center" spacing={2}>
      <Grid item {...regularSize}>
        <SimpleDatePicker
          date={settings.startDate}
          dataTestId="task-custom-repetition-modal-date-picker"
          timezone={timezone}
          handleDateChange={(date) => {
            if (date) {
              // This is the Due Date of the task in the series
              const endOfDay = date.endOf('day').subtract(1, 'minute')
              const next = {
                ...settings,
                startDate: endOfDay
              }
              if (isCustomWeeklyRepetition(next) && next.weekDays.length === 1) {
                next.weekDays = getDefaultWeekdays(next.startDate)
              }
              onChange(next)
            }
          }}
        />
      </Grid>
      <Grid item {...regularSize}>
        <CustomTaskRepetitionDropDownYearly
          translate={(key) => t(`${key}`)}
          data-test-id="task-custom-repetition-unit"
          onChange={(nextRepetition) => {
            if (nextRepetition === settings.repetition) return

            const next = changeCustomTaskRepetition(nextRepetition, settings)
            onChange(next)
          }}
          // Note: Monthly and yearly will supported in future
          value={settings.repetition}
          required
        />
      </Grid>
      <Grid
        item
        {...regularSize}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center'
        }}
      >
        <Box component={'span'} width={50}>
          {t('recurringDialog.interval')}
        </Box>
        <TaskRepetitionInterval
          t={t}
          sx={{
            width: 75,
            '&.MuiFormControl-root': {
              mr: 1
            }
          }}
          settings={settings}
          onChange={(nextInterval) => onChange({...settings, repeatEvery: nextInterval})}
        />
      </Grid>

      {isCustomWeeklyRepetition(settings) && (
        <Grid
          item
          {...regularSize}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center'
          }}
        >
          <Box component={'span'} width={50}>
            {t('recurringDialog.onWeekday')}
          </Box>
          <WeekDaySelect
            selectedDays={settings.weekDays}
            onChange={(weekDays) => {
              if (weekDays.length > 0) onChange({...settings, weekDays})
            }}
          />
        </Grid>
      )}

      <Grid
        item
        {...regularSize}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center'
        }}
      >
        <Box component={'span'} width={50}>
          {t('recurringDialog.ends')}
        </Box>
        <TaskRepetitionEndingOptions
          defaultEnd={defaultEnd}
          onChange={onChange}
          settings={settings}
          plantId={plantId}
        />
      </Grid>

      {settings.repetition && (
        <Grid item {...regularSize}>
          <ProcessWorkOrderDueDatesPreviewLabel
            settings={settings}
            omitFirstOccurrence
            plantId={plantId}
            timezone={timezone}
            defaultStartDate={settings.startDate}
          />
        </Grid>
      )}
    </Grid>
  )
}

type TaskRepetitionDialogProps = {
  timezone: string
  customTaskRepetition: CustomTaskRepetition
  plantId: string
  onClose: () => void
  onChange: (next: CustomTaskRepetition) => void
}

export const TaskRepetitionDialog: React.FC<TaskRepetitionDialogProps> = (props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const [settings, updateCustomTaskRepetition] = useState<CustomTaskRepetition>(
    props.customTaskRepetition
  )

  return (
    <Dialog maxWidth="xs" data-test-id="task-custom-repetition-modal" onClose={props.onClose} open>
      <DialogTitle>{t('recurringDialog.title')}</DialogTitle>
      <DialogContent>
        <CustomRecurringTask
          timezone={props.timezone}
          settings={settings}
          plantId={props.plantId}
          onChange={updateCustomTaskRepetition}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={props.onClose}
          startIcon={<CancelIcon />}
          data-test-id="custom-repetition-cancel-button"
        >
          {t('shiftEvent.action.cancel')}
        </Button>
        <Button
          onClick={() => props.onChange(settings)}
          color="primary"
          startIcon={<CheckIcon />}
          data-test-id="custom-repetition-apply-button"
        >
          {t('shiftEvent.action.apply')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
